import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { Anchor, ArrowRight, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { services, media } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { FadeUp, PageHeader } from "@/components/ui-primitives";
import ContactForm from "@/components/ContactForm";

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const service = services.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const copy = service[lang];
  const Icon = LucideIcons[service.icon] || Anchor;
  const related = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        path={`/services/${slug}`}
        title={copy.name}
        description={copy.short}
        keywords={`${copy.name}, yacht agency Turkey, ${service.slug.replace(/-/g, " ")} Türkiye`}
      />
      <PageHeader
        eyebrow={`${t.nav.services} · 0${services.indexOf(service) + 1}`}
        title={copy.name}
        intro={copy.intro}
        image={media.heroMain}
        video={service.heroVideo || "/assets/videos/provisioning.mp4"}
        poster={media.heroMain}
        testId="service-detail-header"
      />

      <section className="py-24 px-6 sm:px-10 lg:px-16" data-testid="service-detail-body">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8 space-y-16">
            <FadeUp>
              <div>
                <div className="overline mb-5">{t.common.whatWeHandle}</div>
                <h2 className="font-display text-3xl sm:text-4xl text-navy mb-8">
                  {t.common.whatWeHandle}
                </h2>
                <ul className="space-y-4">
                  {copy.handles.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-4 border-b border-navy/10 pb-4"
                    >
                      <Check size={16} className="text-brand mt-1 shrink-0" strokeWidth={1.5} />
                      <span className="text-navy/80 text-base leading-relaxed">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp>
              <div>
                <div className="overline mb-5">{t.common.processSteps}</div>
                <h2 className="font-display text-3xl sm:text-4xl text-navy mb-8">
                  {t.common.processSteps}
                </h2>
                <ol className="space-y-6">
                  {copy.process.map((p, i) => (
                    <li key={i} className="flex gap-6">
                      <span className="font-display text-4xl text-brand/40 leading-none shrink-0 w-12">
                        0{i + 1}
                      </span>
                      <span className="text-navy/80 text-base leading-relaxed pt-1.5">{p}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </FadeUp>

            <FadeUp>
              <div className="glass p-8 border-l-2 border-l-brand">
                <div className="overline mb-4">{t.common.whyItMatters}</div>
                <p className="text-navy/90 text-lg leading-relaxed font-light">
                  {copy.why}
                </p>
              </div>
            </FadeUp>
          </div>

          <aside className="lg:col-span-4 lg:col-start-9">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="glass p-6">
                <Icon size={26} strokeWidth={1.25} className="text-brand mb-5" />
                <div className="overline mb-2">{t.common.overview}</div>
                <p className="text-navy/80 text-sm leading-relaxed mb-6">
                  {copy.short}
                </p>
                <Link
                  to="/contact"
                  state={{ service: copy.name }}
                  className="block text-center bg-brand text-white py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors"
                  data-testid="service-detail-cta"
                >
                  {t.common.requestService}
                </Link>
              </div>

              <div className="glass-light p-6">
                <div className="overline mb-4">{t.common.relatedServices}</div>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/services/${r.slug}`}
                        className="flex items-center justify-between gap-4 text-navy/80 hover:text-brand transition-colors text-sm group"
                      >
                        <span>{r[lang].name}</span>
                        <ArrowRight size={14} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-24 bg-cream border-t border-navy/10 px-6 sm:px-10 lg:px-16" data-testid="service-detail-contact">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-5">{t.common.getInTouch}</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-tight">
              {t.contactPage.formTitle}
            </h2>
            <p className="mt-5 text-navy/70">{t.contactPage.intro}</p>
          </div>
          <div className="lg:col-span-7">
            <ContactForm defaultService={copy.name} />
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
